import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { Delete, Edit, RemoveRedEye, Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../libs/core/components/data-table";
import { useGetProgramsQuery, useDeleteProgramMutation } from "./api/programsApi";
import { Program } from "./types/program";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import AddEditProgram from "./components/AddEditProgram";

const ProgramManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const navigate = useNavigate();

  const { data: programs, isLoading, isError, isFetching } = useGetProgramsQuery();
  const [deleteProgram] = useDeleteProgramMutation();

  const columns: Array<MRT_ColumnDef<Program>> = [
    {
      header: "Name",
      accessorKey: "name",
      size: 200,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {original.name || 'Untitled Program'}
          </Typography>
        );
      },
    },
    {
      header: "Overview of the pillar",
      accessorKey: "description",
      size: 300,
      Cell({ row: { original } }) {
        const overview = original.description || "";
        return (
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 300,
            }}
          >
            {overview.substring(0, 100)}...
          </Typography>
        );
      },
    },
    {
      header: "What the pillar aims to achieve",
      accessorKey: "objectives",
      size: 200,
      Cell({ row: { original } }) {
        return (
          <Chip
            label={`${original.objectives?.length || 0} objectives`}
            color="primary"
            size="small"
          />
        );
      },
    },
    {
      header: "Result Highlight (Key outcomes)",
      accessorKey: "achievements",
      size: 250,
      Cell({ row: { original } }) {
        const achievements = original.achievements || "";
        return (
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 250,
            }}
          >
           {achievements[0]?.program_achievement?.substring(0, 100)}...
          </Typography>
        );
      },
    },
    {
      header: "Actions",
      id: "actions",
      size: 120,
      Cell({ row: { original } }) {
        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/programs/${original.id}`);
              }}
              color="primary"
            >
              <RemoveRedEye />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedProgram(original);
                setOpenDialog(true);
              }}
              color="primary"
            >
              <Edit />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedProgram(original);
                setOpenConfirmModal(true);
              }}
              color="error"
            >
              <Delete />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleDelete = async () => {
    if (selectedProgram) {
      try {
        await deleteProgram(selectedProgram.id.toString()).unwrap();
        setOpenConfirmModal(false);
        setSelectedProgram(null);
      } catch (error) {
        console.error("Failed to delete program:", error);
      }
    }
  };

  const handleSuccess = () => {
    setSelectedProgram(null);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Program Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setSelectedProgram(null);
            setOpenDialog(true);
          }}
        >
          Add New Program
        </Button>
      </Box>

      {isError && (
        <Box
          sx={{
            p: 2,
            bgcolor: "error.light",
            color: "error.contrastText",
            borderRadius: 1,
            mb: 2,
          }}
        >
          <Typography variant="body2">
            Error loading programs. Please check the console for details.
          </Typography>
        </Box>
      )}

      <DataTable
        columns={columns}
        list={programs || []}
count={programs?.length || 0}

        getRowId={(row) => row?.id?.toString() || Math.random().toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        pagination={pagination}
        onPaginationChange={setPagination}
        enableRowSelection={false}
        onRowClick={(row) => navigate(`/programs/${row.id}`)}
      />

      <AddEditProgram
        open={openDialog}
        setModalOpen={setOpenDialog}
        onSuccess={handleSuccess}
        program={selectedProgram}
      />

      <ConfirmModal
        modalOpen={openConfirmModal}
        setModalOpen={setOpenConfirmModal}
        onOk={handleDelete}
        title="Delete Program"
        message={`Are you sure you want to delete "${
          selectedProgram?.name || "this program"
        }"? This action cannot be undone.`}
        type="warning"
      />
    </Box>
  );
};

export default ProgramManager;
